import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ChangePassword.css'; // Reuse ChangePassword styles
import { useLanguage } from '../contexts/LanguageContext';
import SuccessModal from './shared/SuccessModal';
import { Button } from './shared';
import { validatePassword } from '../utils';

const ResetPasswordWithToken = ({ onNavigateToLogin }) => {
  const { t } = useLanguage();
  const [token, setToken] = useState('');
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Extract token from URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      setErrors({ general: 'Kein gültiger Reset-Link. Bitte fordere einen neuen Passwort-Reset an.' });
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.newPassword) {
      newErrors.newPassword = t('changePassword.errors.newRequired') || 'Neues Passwort ist erforderlich';
    } else if (!validatePassword(formData.newPassword)) {
      newErrors.newPassword = t('changePassword.errors.weak') || 'Passwort muss mindestens 8 Zeichen mit Buchstaben, Zahlen und Sonderzeichen enthalten';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('changePassword.errors.confirmRequired') || 'Passwort-Bestätigung ist erforderlich';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = t('changePassword.errors.mismatch') || 'Passwörter stimmen nicht überein';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      setErrors({ general: 'Kein gültiger Reset-Link. Bitte fordere einen neuen Passwort-Reset an.' });
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    setSuccessMessage('');

    try {
      const response = await axios.post('/api/reset-password-with-token', {
        token: token,
        new_password: formData.newPassword,
        confirm_password: formData.confirmPassword
      });

      if (response.data.success) {
        setSuccessMessage('Passwort erfolgreich geändert! Du kannst dich jetzt anmelden.');
        setShowSuccessModal(true);

        // Clear URL token after success
        window.history.replaceState({}, document.title, window.location.pathname);

        // Reset form
        setFormData({
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        setErrors({ general: response.data.message || 'Ein Fehler ist aufgetreten.' });
      }
    } catch (error) {
      console.error('[ResetPasswordWithToken] Error:', error);

      if (error.response && error.response.data) {
        const { message, field } = error.response.data;
        if (field) {
          setErrors({ [field]: message });
        } else {
          setErrors({ general: message || 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.' });
        }
      } else {
        setErrors({ general: 'Netzwerkfehler. Bitte überprüfe deine Internetverbindung.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    return formData.newPassword &&
           formData.confirmPassword &&
           validatePassword(formData.newPassword) &&
           formData.newPassword === formData.confirmPassword &&
           token;
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    if (onNavigateToLogin) {
      onNavigateToLogin();
    }
  };

  return (
    <div className="change-password-container">
      {/* Skip to main content link for keyboard users */}
      <a href="#main-content" className="skip-link">
        {t('a11y.skipToContent') || 'Zum Inhalt springen'}
      </a>

      {/* Navigation Bar */}
      <header className="cp-navigation-bar" role="banner">
        <h1 className="cp-page-title">Passwort zurücksetzen</h1>
      </header>

      {/* Main Content */}
      <main id="main-content" className="cp-main-content" role="main">
        <form onSubmit={handleSubmit} aria-label="Passwort zurücksetzen">
          <div className="cp-form-header">
            <h2 className="cp-form-title">Neues Passwort festlegen</h2>
            <p className="cp-form-subtitle" style={{ marginTop: '8px', fontSize: '14px', color: 'var(--text-secondary)' }}>
              Ein Administrator hat einen Passwort-Reset für Ihr Konto angefordert. Bitte legen Sie ein neues Passwort fest.
            </p>
          </div>

          {/* New Password Field */}
          <div className="cp-text-field-wrapper">
            <label htmlFor="cp-newPassword" className="visually-hidden">
              Neues Passwort
            </label>
            <div className={`cp-text-field-input-wrapper ${errors.newPassword ? 'error' : ''}`}>
              <input
                type={showNewPassword ? 'text' : 'password'}
                id="cp-newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                placeholder="Neues Passwort"
                className="cp-text-field-input-full"
                aria-invalid={errors.newPassword ? 'true' : 'false'}
                aria-describedby={errors.newPassword ? 'newPassword-error' : undefined}
                required
                autoComplete="new-password"
                disabled={!token}
              />
              <button
                type="button"
                className={`cp-visibility-toggle ${showNewPassword ? 'active' : ''}`}
                onClick={() => setShowNewPassword(!showNewPassword)}
                aria-label={showNewPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                aria-pressed={showNewPassword}
              >
                <span aria-hidden="true">👁️</span>
              </button>
            </div>
            {errors.newPassword && (
              <div id="newPassword-error" className="cp-error-message" role="alert">
                {errors.newPassword}
              </div>
            )}
          </div>

          {/* Confirm New Password Field */}
          <div className="cp-text-field-wrapper">
            <label htmlFor="cp-confirmPassword" className="visually-hidden">
              Passwort bestätigen
            </label>
            <div className={`cp-text-field-input-wrapper ${errors.confirmPassword ? 'error' : ''}`}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="cp-confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Passwort bestätigen"
                className="cp-text-field-input-full"
                aria-invalid={errors.confirmPassword ? 'true' : 'false'}
                aria-describedby={errors.confirmPassword ? 'confirmPassword-error' : undefined}
                required
                autoComplete="new-password"
                disabled={!token}
              />
              <button
                type="button"
                className={`cp-visibility-toggle ${showConfirmPassword ? 'active' : ''}`}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                aria-pressed={showConfirmPassword}
              >
                <span aria-hidden="true">👁️</span>
              </button>
            </div>
            {errors.confirmPassword && (
              <div id="confirmPassword-error" className="cp-error-message" role="alert">
                {errors.confirmPassword}
              </div>
            )}
          </div>

          {/* Token Error */}
          {errors.token && (
            <div className="cp-error-message cp-general-error" role="alert" aria-live="polite">
              {errors.token}
            </div>
          )}

          {/* General Error */}
          {errors.general && (
            <div className="cp-error-message cp-general-error" role="alert" aria-live="polite">
              {errors.general}
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="cp-success-message" role="status" aria-live="polite">
              {successMessage}
            </div>
          )}

          {/* Submit Button */}
          <div className="cp-submit-button-wrapper">
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={!isFormValid() || isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? 'Wird gespeichert...' : 'Passwort speichern'}
            </Button>
          </div>

          {/* Back to Login Link */}
          {onNavigateToLogin && (
            <div className="cp-back-link-wrapper">
              <Button
                variant="link"
                onClick={onNavigateToLogin}
                aria-label="Zurück zur Anmeldung"
              >
                Zurück zur Anmeldung
              </Button>
            </div>
          )}
        </form>
      </main>

      {/* Success modal for password change confirmation */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        message={successMessage}
        autoCloseMs={3000}
      />
    </div>
  );
};

export default ResetPasswordWithToken;
